import { TASK_STATUS_OPTIONS, TASK_PRIORITY_OPTIONS } from "@/lib/schemas/task";
import { TAG_SETS } from "@/components/ui/tags/CommonTagChips";

import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Task status, scheduling, assignment, and metadata fields.
 *
 * Each field declaration carries its own `savedViewMembership` listing the
 * saved views it appears in by default; `../task-saved-views.ts` reads
 * those memberships via `collectFieldsForSavedView`.
 */
export const TASK_STATUS_SCHEDULE_FIELDS = defineFieldConfigMap({
  // FIELD: status
  status: {
    type: "select",
    label: "Status",
    isStatusField: true,
    formSpan: 1,
    options: TASK_STATUS_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    })),
    validation: {
      required: false,
    },
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "CheckCircle",
    groupable: true,
    allowedRoles: ["wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "status",
    graphqlSelector: "status",
    savedViewMembership: [
      "all-tasks",
      "my-tasks",
      "high-priority",
      "overdue-tasks",
      "pending-tasks",
      "in-progress-tasks",
      "completed-tasks",
      "cancelled-tasks",
    ],
  },
  // FIELD: priority
  priority: {
    type: "select",
    label: "Priority",
    formSpan: 1,
    options: TASK_PRIORITY_OPTIONS.map((option) => ({
      label: option.label,
      value: option.value,
    })),
    validation: {
      required: false,
    },
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "exact",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "AlertTriangle",
    groupable: true,
    allowedRoles: ["wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "status",
    graphqlSelector: "priority",
    savedViewMembership: [
      "all-tasks",
      "my-tasks",
      "overdue-tasks",
      "pending-tasks",
      "in-progress-tasks",
      "completed-tasks",
      "cancelled-tasks",
    ],
  },
  // FIELD: assigned_users
  assigned_users: {
    type: "multi-user",
    label: "Assigned To",
    placeholder: "Assign task to users...",
    formSpan: 3,
    multiUserConfig: {
      maxUsers: 5,
      showUnassigned: true,
      showAllUsers: false,
    },
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "user-select",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "User",
    groupable: true,
    showOnCard: true,
    cardSpan: 3,
    mobileDetailSectionId: "schedule",
    savedViewMembership: [
      "all-tasks",
      "high-priority",
      "overdue-tasks",
      "pending-tasks",
      "in-progress-tasks",
      "completed-tasks",
      "cancelled-tasks",
    ],
  },
  // FIELD: due_date
  due_date: {
    type: "due-date",
    label: "Due Date",
    placeholder: "Select due date",
    formSpan: 1,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "dateRange",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    icon: "Calendar",
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "schedule",
    graphqlSelector: "due_date",
    /**
     * Tasks "overdue" filter: any task whose due_date is before `now` AND
     * is not already completed. Moved out of `filter-conversion.ts` so the
     * service no longer has to branch on entityName.
     */
    toGraphQLFilter: (value, context) => {
      if (value !== "overdue") return null;
      return {
        due_date: { lt: context.now.toISOString() },
        status: { neq: "completed" },
      };
    },
    savedViewMembership: [
      "all-tasks",
      "my-tasks",
      "high-priority",
      "overdue-tasks",
      "pending-tasks",
      "in-progress-tasks",
      "completed-tasks",
      "cancelled-tasks",
    ],
  },
  // FIELD: tags
  tags: {
    type: "tags",
    label: "Tags",
    placeholder: "Add custom tags...",
    formSpan: 3,
    tagsConfig: {
      commonTags: [
        ...TAG_SETS.TASK,
        ...TAG_SETS.SCHOOLEY_MITCHELL,
        ...TAG_SETS.WEST_TENNESSEE_CONSULTING,
      ],
      allowCustom: true,
      maxTags: 10,
    },
    sortable: false,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "multiSelect",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "Tag",
    mobileDetailSectionId: "tags",
  },
  // FIELD: user_id
  user_id: {
    type: "text",
    label: "Creator",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "app_users",
      referencedColumn: "id",
      constraintName: "contact_tasks_user_id_fkey",
      onDelete: "CASCADE",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Auto-populated with current user",
    formOnly: true,
    excludeFromApi: false,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "exact",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "User",
    mobileDetailSectionId: "metadata",
  },
  // FIELD: created_at
  created_at: {
    type: "datetime",
    label: "Created",
    // DB-managed (default now()). Display-only — never sent on write.
    excludeFromApi: true,
    autoFill: true,
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: false,
    width: 160,
    minWidth: 120,
    maxWidth: 220,
    icon: "Calendar",
    datetimePresets: {
      enabled: true,
      presets: [
        "today",
        "yesterday",
        "last7days",
        "last30days",
        "thismonth",
        "lastmonth",
        "thisyear",
        "lastyear",
      ],
      showCustomRange: true,
    },
    mobileDetailSectionId: "metadata",
    graphqlSelector: "created_at",
    savedViewMembership: [
      "all-tasks",
      "my-tasks",
      "pending-tasks",
      "in-progress-tasks",
      "completed-tasks",
      "cancelled-tasks",
    ],
  },
});
